/* First-party analytics + GTM bridge.
   GTM (GTM-TGDTGZ29) is the single source of truth for GA4 — every event goes
   to dataLayer (GTM triggers) and /api/track (AI OS). No direct gtag calls. */
import { useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/* First-touch attribution (per session): UTM params + referrer → so the Admin
   AI OS can answer "what happened when we posted on social / sent an email". */
const ATTR_KEY = 'kx_attribution_v1';
function getAttribution() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(ATTR_KEY) || 'null');
    if (stored) return stored;
    const q = new URLSearchParams(window.location.search);
    let refHost = '';
    try { refHost = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, '') : ''; } catch { /* noop */ }
    if (refHost && refHost === window.location.hostname.replace(/^www\./, '')) refHost = '';
    const attr = {
      utm_source: q.get('utm_source') || '',
      utm_medium: q.get('utm_medium') || (q.get('gclid') ? 'cpc' : (q.get('fbclid') ? 'social' : '')),
      utm_campaign: q.get('utm_campaign') || '',
      ref: (q.get('utm_source') || refHost || 'direct').toLowerCase(),
      landing: window.location.pathname,
    };
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    return attr;
  } catch {
    return { ref: 'direct', utm_source: '', utm_medium: '', utm_campaign: '' };
  }
}
export { getAttribution };

/* Internal traffic exclusion: anyone who opens the admin Hub/OS is staff.
   Their visits never pollute GA4, GTM or first-party stats again (device-persistent). */
function isInternal() {
  try {
    if (/^\/(hub|admin|os)(\/|$)/.test(window.location.pathname)) {
      localStorage.setItem('kx_internal', '1');
    }
    return localStorage.getItem('kx_internal') === '1';
  } catch { return false; }
}
export { isInternal };

/* Anonymous visitor (device-persistent) + session ids → Business Pulse can count
   real visitors, live sessions and journeys without any PII. */
function getIds() {
  try {
    let vid = localStorage.getItem('kx_vid');
    if (!vid) { vid = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('kx_vid', vid); }
    let sid = sessionStorage.getItem('kx_sid');
    if (!sid) { sid = Math.random().toString(36).slice(2, 10) + Date.now().toString(36); sessionStorage.setItem('kx_sid', sid); }
    return { vid, sid };
  } catch { return { vid: '', sid: '' }; }
}

function beacon(data) {
  try {
    const a = getAttribution();
    const payload = JSON.stringify({
      path: window.location.pathname,
      ref: a.ref, utm_source: a.utm_source, utm_medium: a.utm_medium, utm_campaign: a.utm_campaign,
      ...getIds(),
      ...data,
    });
    const url = `${API}/api/track`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true });
    }
  } catch { /* never block UX */ }
}

export function trackEvent(name, params = {}) {
  if (isInternal()) return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...params });
  } catch { /* noop */ }
  // GA4 direct (gtag is the source of truth) — enriched with page + attribution.
  try {
    if (window.gtag) {
      const a = getAttribution();
      window.gtag('event', name, {
        page_path: window.location.pathname,
        traffic_ref: a.ref, campaign_name: a.utm_campaign || undefined,
        ...params,
      });
    }
  } catch { /* noop */ }
  beacon({ type: 'event', name, ...params });
}

let initialLoadTracked = false;
export function trackPageView(path) {
  resetEngagement(path);
  // Self-referencing canonical on every SPA navigation (SEO.jsx overrides with curated paths).
  try {
    const c = document.getElementById('kx-canonical');
    if (c) {
      const h = /(^|\.)kitxens\.com$/.test(window.location.hostname) ? 'https://www.kitxens.com' : window.location.origin;
      c.setAttribute('href', h + (path.length > 1 ? path.replace(/\/+$/, '') : path));
    }
  } catch { /* noop */ }
  if (isInternal()) {
    // Staff sessions never reach Clarity recordings either.
    try { if (window.clarity) window.clarity('stop'); } catch { /* noop */ }
    return;
  }
  // Clarity auto-detects SPA history changes; the page tag makes filtering effortless.
  try { if (window.clarity) window.clarity('set', 'page', path); } catch { /* noop */ }
  /* GA4 page_views: initial load is measured by gtag('config') in index.html;
     SPA route changes are measured by GA4 Enhanced Measurement (browser
     history events). The spa_page_view dataLayer push below stays available
     for GTM triggers but no GA4 tag should consume it (avoids duplicates). */
  if (!initialLoadTracked) {
    initialLoadTracked = true;
    beacon({ type: 'page_view', path });
    return;
  }
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'spa_page_view',
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  } catch { /* noop */ }
  beacon({ type: 'page_view', path });
}

/* Engagement engine: per-route scroll depth milestones (25/50/75/90) and a
   60-second engaged timer. Resets on every SPA navigation — each page tells
   its own story in GA4 (scroll_depth, engaged_60s). */
let engState = { path: '', fired: {}, timer: null, t0: 0 };
function resetEngagement(path) {
  try {
    clearTimeout(engState.timer);
    engState = { path, fired: {}, timer: null, t0: Date.now() };
    engState.timer = setTimeout(() => {
      if (document.visibilityState === 'visible') emitEngagement('engaged_60s', { seconds: 60 });
    }, 60000);
  } catch { /* noop */ }
}
function emitEngagement(name, params) {
  if (engState.fired[name + (params.percent || '')] || isInternal()) return;
  engState.fired[name + (params.percent || '')] = true;
  const rich = { page_path: engState.path, page_title: document.title, ...params };
  try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: name, ...rich }); } catch { /* noop */ }
  try { if (window.gtag) window.gtag('event', name, rich); } catch { /* noop */ }
  beacon({ type: 'event', name, ...params, depth: params.percent || 0 });
}
let engBound = false;
export function initEngagementTracking() {
  if (engBound) return;
  engBound = true;
  window.addEventListener('scroll', () => {
    try {
      const h = document.documentElement;
      const d = Math.round(((window.scrollY + window.innerHeight) / h.scrollHeight) * 100);
      for (const m of [25, 50, 75, 90]) {
        if (d >= m) emitEngagement('scroll_depth', { percent: m });
      }
    } catch { /* noop */ }
  }, { passive: true });
}

/* Global listener: WhatsApp / phone / email / social / PDF / nav / service / outbound
   clicks — zero per-component wiring. */
const SOCIAL_HOSTS = { 'linkedin.com': 'linkedin', 'instagram.com': 'instagram', 'facebook.com': 'facebook', 'x.com': 'x', 'twitter.com': 'x', 'youtube.com': 'youtube', 'tiktok.com': 'tiktok' };
const SERVICE_PATHS = ['/services', '/ai-solutions', '/index-ai', '/ai-receptionist', '/zero-commission-ordering', '/restaurant-websites', '/data-analytics', '/dashboards', '/restaurant-technology', '/how-it-works'];
let clicksBound = false;
export function initGlobalClickTracking() {
  if (clicksBound) return;
  clicksBound = true;
  document.addEventListener('click', (ev) => {
    if (isInternal()) return;
    const tagged = ev.target.closest && ev.target.closest('[data-analytics]');
    const a = ev.target.closest && ev.target.closest('a[href]');
    const emit = (name, rich) => {
      try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: name, ...rich }); } catch { /* noop */ }
      try { if (window.gtag) window.gtag('event', name, rich); } catch { /* noop */ }
      beacon({ type: 'event', name, ...rich });
    };
    // Explicitly tagged CTAs win: <button data-analytics="pricing_cta_click" data-label="Pro plan">
    if (tagged) {
      const name = tagged.getAttribute('data-analytics');
      emit(name, { label: tagged.getAttribute('data-label') || (tagged.textContent || '').trim().slice(0, 60), page_path: window.location.pathname });
      if (!a) return;
    }
    if (!a) return;
    let href = a.getAttribute('href') || '';
    // Kitxens outbound redirector: /go?u=<encoded destination> — classify the real target.
    if (href.startsWith('/go?') || href.includes('/go?u=')) {
      try {
        const u = new URL(href, window.location.origin).searchParams.get('u');
        if (u) href = u;
      } catch { /* noop */ }
    }
    const text = (a.textContent || '').trim().slice(0, 60);
    const rich = { link_url: href.slice(0, 400), link_text: text, page_path: window.location.pathname, page_title: document.title };
    if (href.startsWith('tel:')) return emit('contact_phone', rich);
    if (href.startsWith('mailto:')) return emit('contact_email', rich);
    if (href.includes('wa.me/') || href.includes('api.whatsapp.com')) return emit('contact_whatsapp', rich);
    if (/\.pdf(\?|$)/i.test(href)) return emit('file_download', { ...rich, file_extension: 'pdf' });
    if (/^https?:\/\//.test(href) && !href.includes(window.location.hostname) && !href.includes('kitxens.com')) {
      try {
        const host = new URL(href).hostname.replace(/^www\./, '');
        for (const [h, network] of Object.entries(SOCIAL_HOSTS)) {
          if (host === h || host.endsWith(`.${h}`)) return emit('social_click', { ...rich, network });
        }
      } catch { /* noop */ }
      return emit('outbound_click', rich);
    }
    // Internal links: navigation menu vs. service discovery.
    const path = href.split('?')[0].split('#')[0];
    if (a.closest('nav, header')) return emit('nav_click', rich);
    if (SERVICE_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) {
      return emit('service_click', { ...rich, service: path });
    }
    if (window.location.pathname === '/pricing' && (a.closest('button') || a.getAttribute('role') === 'button')) {
      emit('pricing_cta_click', rich);
    }
    return undefined;
  }, { capture: true, passive: true });
  // Buttons on /pricing (non-anchor CTAs) → pricing_cta_click.
  document.addEventListener('click', (ev) => {
    if (isInternal() || window.location.pathname !== '/pricing') return;
    const b = ev.target.closest && ev.target.closest('button');
    if (!b || (ev.target.closest && ev.target.closest('a[href]'))) return;
    const rich = { label: (b.textContent || '').trim().slice(0, 60), page_path: '/pricing' };
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'pricing_cta_click', ...rich }); } catch { /* noop */ }
    try { if (window.gtag) window.gtag('event', 'pricing_cta_click', rich); } catch { /* noop */ }
    beacon({ type: 'event', name: 'pricing_cta_click', ...rich });
  }, { capture: true, passive: true });
}

/* Real read-time tracker: active seconds + max scroll depth per article. */
export function useReadTracker(slug) {
  useEffect(() => {
    if (!slug) return undefined;
    let active = 0;
    let maxDepth = 0;
    let sent = false;
    let last = Date.now();
    let visible = document.visibilityState === 'visible';

    const tick = setInterval(() => {
      if (visible) active += (Date.now() - last) / 1000;
      last = Date.now();
    }, 1000);
    const onVis = () => { visible = document.visibilityState === 'visible'; last = Date.now(); };
    const onScroll = () => {
      const h = document.documentElement;
      const d = Math.round(((window.scrollY + window.innerHeight) / h.scrollHeight) * 100);
      if (d > maxDepth) maxDepth = Math.min(d, 100);
    };
    const send = () => {
      if (sent || active < 3) return;
      sent = true;
      beacon({ type: 'read', slug, seconds: Math.round(active), depth: maxDepth, path: `/magazine/${slug}` });
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', send);
    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', send);
      send();
    };
  }, [slug]);
}
